import { useEffect, useState } from "react";
import styled from "styled-components";
import { styled as muiStyled } from "@mui/material/styles";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";

import { Grid } from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";

import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AttachFileIcon from "@mui/icons-material/AttachFile";

import { updateShippingOrder } from "src/api/shippingOrder";

import font from "src/styles/font";
import { checkNotEmpty } from "src/common";
import { Black, Primary } from "src/styles/color";

import Dialog from "src/components/core/Dialog";
import Button from "src/components/core/Button";
import TextField from "src/components/core/TextField";
import UploadPdf from "src/components/upload/dropzone/UploadPdf";
import UploadFile from "src/components/upload/dropzone/UploadFile";
import {
  SpanLabel,
  SpanRemarkLabel,
  StyledDatePicker,
  StyledTextField,
  TextFieldDialog,
} from "src/modules/order/shipping-order/components";

const CloseButton = styled(IconButton)`
  &.MuiIconButton-root {
    color: ${Black};
  }
`;

const StyledDialog = muiStyled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 16,
  },
  "& .MuiDialogContent-root": {
    maxHeight: "500px",
    border: 0,
    padding: 24,
    paddingTop: 0,
  },
  "& .MuiDialog-container": {
    "& .MuiPaper-root": {
      width: "100%",
      maxWidth: 640,
    },
  },
  "& .MuiDialogActions-root": {
    justifyContent: "center",
  },
  "& .MuiDialogActions-root>:not(:first-of-type)": {
    marginLeft: 24,
  },

  [theme.breakpoints.down("sm")]: {
    "& .MuiPaper-root": {
      margin: 24,
    },
    "& .MuiDialogContent-root": {
      padding: 16,
      paddingTop: 0,
    },
    "& .MuiDialogActions-root": {
      padding: 16,
    },
  },
}));

const VcDialog = ({
  onClose,
  onSuccess,
  orderDetail,
  mode = "add",
  setIsLoading,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const [shippingDetail, setShippingDetail] = useState({});

  useEffect(() => {
    if (!!orderDetail) {
      setShippingDetail({
        ...orderDetail,
        documents: {
          ...orderDetail.documents,
          xsurface: {
            ...orderDetail.documents?.xsurface,
            vc: {
              ...orderDetail.documents?.xsurface?.vc,
              xsurfaceVcAdditionalFile:
                orderDetail?.documents?.xsurface?.vc?.xsurfaceVcAdditionalFile,
              xsurfaceVcFile:
                orderDetail?.documents?.xsurface?.vc?.xsurfaceVcFile,
              vcNo: orderDetail?.documents?.xsurface?.vc?.vcNo,
              vcDate: orderDetail?.documents?.xsurface?.vc?.vcDate,
            },
          },
        },
      });
    }
  }, [orderDetail]);

  const onSubmit = async () => {
    try {
      setIsLoading(true);
      const payload = {
        ...shippingDetail,
        order: shippingDetail?.order?._id,
      };

      const data = new FormData();
      for (const field in payload) {
        if (
          field === "order" ||
          field === "_id" ||
          field === "method" ||
          field === "note" ||
          field === "seller"
        ) {
          data.append(field, payload[field]);
        } else {
          data.append(field, JSON.stringify(payload[field]));
        }
      }

      let document = JSON.parse(data.get("documents"));
      delete document.xsurface.vc.xsurfaceVcFile;
      delete document.xsurface.vc.xsurfaceVcAdditionalFile;
      data.set("documents", JSON.stringify(document));

      let fulfillmentProcess = JSON.parse(data.get("fulfillmentProcess"));
      if (fulfillmentProcess?.[0]?.deliveryProofs?.length > 0) {
        let formatDeliveryProofs = fulfillmentProcess?.[0]?.deliveryProofs;

        formatDeliveryProofs?.map((item, index) => {
          let format = item;
          format.deliveryProof = item?.deliveryProof?._id;
          return format;
        });

        fulfillmentProcess[0] = {
          ...fulfillmentProcess[0],
          ...{ deliveryProofs: formatDeliveryProofs },
        };
      }
      if (fulfillmentProcess?.[0]?.shippingProofs?.length > 0) {
        let formatShippingProofs = fulfillmentProcess?.[0]?.shippingProofs;

        formatShippingProofs?.map((item, index) => {
          let format = item;
          format.shippingProof = item?.shippingProof?._id;
          return format;
        });

        fulfillmentProcess[0] = {
          ...fulfillmentProcess[0],
          ...{ shippingProofs: formatShippingProofs },
        };
      }
      data.set("fulfillmentProcess", JSON.stringify(fulfillmentProcess));

      if (
        typeof payload.documents.xsurface.vc.xsurfaceVcFile === "object" &&
        payload.documents.xsurface.vc.xsurfaceVcFile !== null
      ) {
        if (payload.documents.xsurface.vc.xsurfaceVcFile.path) {
          data.append(
            "documents.xsurface.vc.xsurfaceVcFile",
            renameFile(payload.documents.xsurface.vc.xsurfaceVcFile),
          );
        } else {
          document.xsurface.vc.xsurfaceVcFile =
            payload.documents.xsurface.vc.xsurfaceVcFile._id;
          data.set("documents", JSON.stringify(document));
        }
      }
      if (
        typeof payload.documents.xsurface.vc.xsurfaceVcAdditionalFile ===
          "object" &&
        payload.documents.xsurface.vc.xsurfaceVcAdditionalFile !== null
      ) {
        if (payload.documents.xsurface.vc.xsurfaceVcAdditionalFile.path) {
          data.append(
            "documents.xsurface.vc.xsurfaceVcAdditionalFile",
            renameFile(payload.documents.xsurface.vc.xsurfaceVcAdditionalFile),
          );
        } else {
          document.xsurface.vc.xsurfaceVcAdditionalFile =
            payload.documents.xsurface.vc.xsurfaceVcAdditionalFile._id;
          data.set("documents", JSON.stringify(document));
        }
      }
      data.append("isDocument", true);
      await updateShippingOrder(orderDetail._id, data);
      await onSuccess();
      enqueueSnackbar(`อัพเดทข้อมูลใบเสนอราคา สำเร็จ`, {
        variant: "success",
      });
    } catch (err) {
      setIsLoading(false);
      enqueueSnackbar(err.response.data.message, {
        variant: "error",
      });
    }
  };

  const isFormValid = () => {
    return (
      shippingDetail?.documents?.xsurface?.vc?.vcDate &&
      shippingDetail?.documents?.xsurface?.vc?.vcNo &&
      shippingDetail?.documents?.xsurface?.vc?.xsurfaceVcFile
      //   ||
      // (!shippingDetail?.documents?.customer?.inv?.invDate &&
      //   !shippingDetail?.documents?.customer?.inv?.invNo &&
      //   !shippingDetail?.documents?.customer?.inv?.invFile)
    );
  };

  const renameFile = (file) => {
    const randomPart = Math.floor(Math.random() * 100000);
    let newName = `VC${dayjs().format("DDMMYY")}-${randomPart}`;
    return new File([file], newName, {
      type: file.type,
      lastModified: file.lastModified,
    });
  };

  return (
    <StyledDialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={true}
    >
      <DialogTitle
        sx={{
          p: {
            sm: 3,
            xs: 2,
          },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          fontSize: {
            sm: font.size.lg,
            xs: font.size.md,
          },
          fontWeight: 600,
        }}
      >
        {mode === "add" ? "เพิ่มเอกสาร" : "แก้ไขเอกสาร"}
        <CloseButton aria-label="close" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </CloseButton>
      </DialogTitle>

      <DialogContent>
        <Grid container xs={12}>
          <Grid
            item
            xs={12}
            sm={6}
            sx={{ pr: { xs: 0, sm: 2 }, mb: { xs: 2, sm: 0 } }}
          >
            <SpanLabel isMargin>
              วันที่ VC <span>*</span>
            </SpanLabel>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <StyledDatePicker
                inputFormat="DD-MM-YYYY"
                value={shippingDetail?.documents?.xsurface?.vc?.vcDate}
                onChange={(value) => {
                  setShippingDetail((prevState) => {
                    const updateShipping = prevState;
                    updateShipping.documents.xsurface.vc.vcDate =
                      value !== null ? value.$d : "";
                    return {
                      ...prevState,
                      ...updateShipping,
                    };
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    sx={{
                      width: "100%",
                      height: {
                        xs: 40,
                        sm: 48,
                      },
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={6} sx={{ pl: { xs: 0, sm: 2 } }}>
            <SpanLabel isMargin>
              {`VC No.`} <span>*</span>
            </SpanLabel>
            <TextFieldDialog
              variant="outlined"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={shippingDetail?.documents?.xsurface?.vc?.vcNo}
              onChange={({ target }) =>
                setShippingDetail((prevState) => {
                  const updateShipping = prevState;
                  updateShipping.documents.xsurface.vc.vcNo = target.value;
                  return {
                    ...prevState,
                    ...updateShipping,
                  };
                })
              }
            />
          </Grid>
        </Grid>

        <Grid container xs={12} mt={2}>
          <Grid item xs={12}>
            <SpanLabel isMargin>
              {`ใบ VC`} <span>*</span>
              <SpanRemarkLabel> {`(.pdf)`}</SpanRemarkLabel>
            </SpanLabel>
            <StyledTextField
              variant="outlined"
              fullWidth
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <>
                    {checkNotEmpty(
                      shippingDetail?.documents?.xsurface?.vc?.xsurfaceVcFile,
                    ) ? (
                      <IconButton
                        onClick={() => {
                          const newShipping = shippingDetail;
                          shippingDetail.documents.xsurface.vc.xsurfaceVcFile =
                            "";
                          setShippingDetail({ ...newShipping });
                        }}
                      >
                        <DeleteOutlineIcon
                          sx={{
                            color: Primary,
                            cursor: "pointer",
                          }}
                        />
                      </IconButton>
                    ) : (
                      <UploadPdf
                        callBackFile={(fileCallBlack) => {
                          const newShipping = shippingDetail;
                          newShipping.documents.xsurface.vc.xsurfaceVcFile =
                            fileCallBlack;
                          setShippingDetail({ ...newShipping });
                        }}
                        closeAlert={false}
                      >
                        <IconButton>
                          <AttachFileIcon
                            sx={{
                              color: Black,
                              cursor: "pointer",
                              fontSize: font.size.lg,
                              ":hover": {
                                color: "#e04132",
                              },
                            }}
                          />
                        </IconButton>
                      </UploadPdf>
                    )}
                  </>
                ),
              }}
              InputLabelProps={{ shrink: true }}
              value={
                typeof shippingDetail?.documents?.xsurface?.vc
                  ?.xsurfaceVcFile !== "object"
                  ? shippingDetail?.documents?.xsurface?.vc?.xsurfaceVcFile
                  : (shippingDetail?.documents?.xsurface?.vc?.xsurfaceVcFile
                      ?.path ??
                    shippingDetail?.documents?.xsurface?.vc?.xsurfaceVcFile
                      ?.originalFileName)
              }
            />
          </Grid>
        </Grid>

        <Grid container xs={12} mt={2}>
          <Grid item xs={12}>
            <SpanLabel isMargin>
              {`เอกสารเพิ่มเติม`}
              <SpanRemarkLabel> {`(.pdf, .jpg, .jpec, .png)`}</SpanRemarkLabel>
            </SpanLabel>
            <StyledTextField
              variant="outlined"
              fullWidth
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <>
                    {checkNotEmpty(
                      shippingDetail?.documents?.xsurface?.vc
                        ?.xsurfaceVcAdditionalFile,
                    ) ? (
                      <IconButton
                        onClick={() => {
                          const newShipping = shippingDetail;
                          shippingDetail.documents.xsurface.vc.xsurfaceVcAdditionalFile =
                            "";
                          setShippingDetail({ ...newShipping });
                        }}
                      >
                        <DeleteOutlineIcon
                          sx={{
                            color: Primary,
                            cursor: "pointer",
                          }}
                        />
                      </IconButton>
                    ) : (
                      <UploadFile
                        callBackFile={(fileCallBlack) => {
                          const newShipping = shippingDetail;
                          newShipping.documents.xsurface.vc.xsurfaceVcAdditionalFile =
                            fileCallBlack;
                          setShippingDetail({ ...newShipping });
                        }}
                        closeAlert={false}
                        acceptFileType={[".pdf", ".jpg", ".jpeg", ".png"]}
                      >
                        <IconButton>
                          <AttachFileIcon
                            sx={{
                              color: Black,
                              cursor: "pointer",
                              fontSize: font.size.lg,
                              ":hover": {
                                color: "#e04132",
                              },
                            }}
                          />
                        </IconButton>
                      </UploadFile>
                    )}
                  </>
                ),
              }}
              InputLabelProps={{ shrink: true }}
              value={
                typeof shippingDetail?.documents?.xsurface?.vc
                  ?.xsurfaceVcAdditionalFile !== "object"
                  ? shippingDetail?.documents?.xsurface?.vc
                      ?.xsurfaceVcAdditionalFile
                  : (shippingDetail?.documents?.xsurface?.vc
                      ?.xsurfaceVcAdditionalFile?.path ??
                    shippingDetail?.documents?.xsurface?.vc
                      ?.xsurfaceVcAdditionalFile?.originalFileName)
              }
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          variant="outlined"
          color="secondary"
          size="large"
          onClick={() => onClose()}
          fullWidth
          sx={{ maxWidth: { xs: 135.5, sm: 180 }, height: { xs: 32, sm: 48 } }}
        >
          ยกเลิก
        </Button>

        <Button
          variant="contained"
          size="large"
          onClick={() => onSubmit()}
          fullWidth
          sx={{ maxWidth: { xs: 135.5, sm: 180 }, height: { xs: 32, sm: 48 } }}
          disabled={!isFormValid()}
        >
          ยืนยัน
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default VcDialog;
